(() => {
  if (customElements.get('tachan-bundle')) return;
  class TachanBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent); } catch { return; }
      this.baseSelect = this.querySelector('[data-bundle-base]');
      this.extraSelect = this.querySelector('[data-bundle-extra]');
      this.button = this.querySelector('[data-bundle-submit]');
      this.buttonLabel = this.querySelector('[data-bundle-submit-label]');
      this.status = this.querySelector('[data-bundle-status]');
      this.form = this.querySelector('[data-bundle-form]');
      this.pending = false;
      this.uncertain = false;
      this.stockState = 'loading';
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.baseSelect.addEventListener('change', () => this.update(), { signal });
      this.extraSelect.addEventListener('change', () => this.update(), { signal });
      this.querySelector('[data-bundle-stock-retry]')?.addEventListener('click', () => this.hydrateStock(true), { signal });
      this.form.addEventListener('submit', (event) => this.submit(event), { signal });
      this.update();
      this.hydrateStock();
    }
    disconnectedCallback() { this.stockRevision = (this.stockRevision || 0) + 1; this.controller?.abort(); this.ready = false; }
    async hydrateStock(force = false) {
      const revision = this.stockRevision = (this.stockRevision || 0) + 1;
      this.stockState = 'loading';
      this.update();
      try {
        const results = await Promise.all(['base', 'extra'].map(kind => window.TachanCatalog.load({ productId: this.config[kind + 'ProductId'], productUrl: this.config[kind + 'ProductUrl'], variantIds: this.config[kind].map(variant => variant.id), currency: this.config.currency, cartUrl: this.config.cartUrl, force })));
        if (revision !== this.stockRevision || !this.isConnected) return;
        ['base', 'extra'].forEach((kind, index) => {
          this.config[kind] = this.config[kind].map(variant => {
            const fresh = results[index].variants.find(item => item.id === String(variant.id));
            return { ...variant, available: fresh.available, price: fresh.price, image: fresh.image || variant.image };
          });
          const select = kind === 'base' ? this.baseSelect : this.extraSelect;
          for (const option of select.options) {
            const variant = this.variant(kind, option.value);
            const label = option.dataset.tbLabel || option.textContent.split(' — ')[0].trim();
            option.dataset.tbLabel = label;
            option.disabled = !variant?.available;
            if (variant) option.textContent = label + ' — ' + this.money(variant.price) + (variant.available ? '' : ' · ' + this.config.labels.unavailable);
          }
        });
        this.stockState = 'ready';
        this.update();
      } catch {
        if (revision !== this.stockRevision || !this.isConnected) return;
        this.stockState = 'error';
        this.update();
      }
    }
    variant(kind, id) { return this.config[kind].find((variant) => String(variant.id) === String(id)); }
    money(cents) {
      try { return new Intl.NumberFormat(this.config.locale || 'es-ES', { style: 'currency', currency: this.config.currency || 'EUR' }).format(cents / 100); }
      catch { return (cents / 100).toFixed(2) + ' ' + (this.config.currency || 'EUR'); }
    }
    selected() {
      return { base: this.variant('base', this.baseSelect.value), extra: this.variant('extra', this.extraSelect.value) };
    }
    updateImage(selector, variant) {
      const image = this.querySelector(selector);
      if (image && variant?.image) { image.removeAttribute('srcset'); image.src = variant.image; }
    }
    update() {
      const { base, extra } = this.selected();
      const available = this.stockState === 'ready' && Boolean(base?.available && extra?.available && base.id !== extra.id);
      const locked = this.pending || this.uncertain || this.stockState !== 'ready';
      this.baseSelect.disabled = locked;
      this.extraSelect.disabled = locked;
      this.button.disabled = locked || this.config.preview || !available;
      this.buttonLabel.textContent = this.pending ? this.config.labels.adding : this.stockState === 'loading' ? this.config.stock.loading : this.stockState === 'error' ? this.config.stock.pending : this.config.preview ? (this.config.labels.preview || this.config.labels.unavailable) : this.uncertain ? (this.config.labels.viewCart || this.config.labels.error) : available ? this.config.labels.add : this.config.labels.unavailable;
      const stock = this.querySelector('[data-bundle-stock]');
      if (stock) stock.textContent = this.stockState === 'loading' ? this.config.stock.loading : this.stockState === 'error' ? this.config.stock.error : available ? '' : this.config.stock.unavailable;
      const retry = this.querySelector('[data-bundle-stock-retry]');
      if (retry) retry.hidden = this.stockState !== 'error' || this.pending || this.uncertain;
      if (!base || !extra) return;
      const regular = base.price + extra.price;
      const discount = Math.min(Math.max(Number(this.config.discount) || 0, 0), extra.price);
      this.querySelector('[data-bundle-total]').textContent = this.money(regular - discount);
      this.querySelector('[data-bundle-regular]').textContent = this.money(regular);
      const savings = this.querySelector('[data-bundle-savings]');
      if (savings) savings.textContent = this.config.labels.save + ' ' + this.money(discount);
      this.dataset.discounted = String(discount > 0);
      this.updateImage('[data-bundle-base-image]', base);
      this.updateImage('[data-bundle-extra-image]', extra);
      if (!this.pending && !this.uncertain) this.status.textContent = '';
    }
    async submit(event) {
      event.preventDefault();
      if (this.pending || this.uncertain || this.config.preview || this.stockState !== 'ready') return;
      const { base, extra } = this.selected();
      if (!base?.available || !extra?.available || base.id === extra.id) { this.status.textContent = this.config.labels.unavailable; return; }
      this.pending = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      this.update();
      const root = this.config.root.endsWith('/') ? this.config.root : this.config.root + '/';
      let responseReceived = false;
      let responseStatus;
      try {
        const response = await fetch(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: base.id, quantity: 1 }, { id: extra.id, quantity: 1 }] }) });
        responseReceived = true;
        responseStatus = response.status;
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error(typeof data?.description === 'string' ? data.description : this.config.labels.error);
        this.status.textContent = this.config.labels.added;
        document.dispatchEvent(new CustomEvent('tachan:cart-added', { detail: { items: data?.items || [], source: 'bundle' } }));
        window.location.assign(this.config.cartUrl || root + 'cart');
      } catch (error) {
        this.pending = false;
        this.uncertain = !responseReceived;
        this.removeAttribute('aria-busy');
        this.update();
        if (this.uncertain) {
          const spanish = String(this.config.locale || 'es').startsWith('es');
          this.status.textContent = (this.config.labels.networkError || (spanish ? 'No hemos podido confirmar el resultado. Revisa el carrito antes de volver a añadir este pack.' : 'We could not confirm the result. Check your cart before adding this bundle again.')) + ' ';
          const link = document.createElement('a');
          link.href = this.config.cartUrl || root + 'cart';
          link.textContent = this.config.labels.viewCart || (spanish ? 'Revisar carrito' : 'Review cart');
          this.status.append(link);
        } else {
          this.status.textContent = error instanceof Error && error.message ? error.message : this.config.labels.error;
          if (responseStatus === 422) {
            this.status.textContent += ' ';
            const link = document.createElement('a');
            link.href = this.config.cartUrl || root + 'cart';
            link.textContent = this.config.labels.viewCart || (String(this.config.locale || 'es').startsWith('es') ? 'Revisar carrito' : 'Review cart');
            this.status.append(link);
          }
        }
      }
    }
  }
  customElements.define('tachan-bundle', TachanBundle);
})();
